(() => {
    const SUBHEADS = new Set(["afterpay", "other bnpl providers"]);

    // Action-card label → preferred section heading matches
    const SCROLL_ALIASES = {
        "submit a warranty claim": [
            "ready to file a claim",
            "ready to file a claim?",
            "warranty policy",
            "submit a warranty request",
        ],
        "start a return": [
            "return policy",
            "returns & warranty",
            "returns and warranty",
        ],
        "size charts": [
            "product size charts",
            "product size chart",
            "size chart",
        ],
        "size chart": [
            "product size charts",
            "product size chart",
            "size charts",
        ],
        "product material, styles, & care": [
            "product material, styles, and care",
            "product material, styles & care",
            "product material styles & care",
        ],
    };

    const normalize = (value) =>
        value.replace(/\s+/g, " ").trim().toLowerCase();

    const slugify = (value) =>
        normalize(value)
            .replace(/&/g, " and ")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

    const isCustomerCarePage = () =>
        Boolean(
            document.querySelector(
                ".side-nav-block, .side-nav-layout, nav.side-nav",
            ),
        );

    const ACTION_CARD_GRID_GAP = "15px";

    const isInsideSkipList = (el) => {
        const list = el.closest("ul, ol");
        return Boolean(list?.querySelector("a"));
    };

    const isInsideActionCard = (el) =>
        Boolean(el.closest(".group-block")?.querySelector(".icon-block"));

    const getContentRoot = () =>
        document.querySelector(".layout-panel-flex:has(> .side-nav-block)") ||
        document.body;

    const markSubheads = (root = document) => {
        if (!isCustomerCarePage()) return;

        root.querySelectorAll(
            ".details-content strong, .details-content b, .details-content p",
        ).forEach((el) => {
            const label = normalize(el.textContent || "");
            if (!SUBHEADS.has(label)) return;

            el.classList.add("cc-subhead");
            if (el.tagName !== "P")
                el.closest("p")?.classList.add("cc-subhead");
        });
    };

    const getHeadingLabel = (el) => {
        if (el.classList?.contains("details__header")) {
            const clone = el.cloneNode(true);
            clone
                .querySelectorAll(".svg-wrapper, .details__icon, svg, img")
                .forEach((node) => node.remove());
            return normalize(clone.textContent || "");
        }
        return normalize(el.textContent || "");
    };

    const collectHeadings = () =>
        [
            ...getContentRoot().querySelectorAll(
                ":is(h1, h2, h3, h4, h5, h6), .text-block:is(.h1, .h2, .h3, .h4, .h5, .h6), .details__header, .text-block :is(h1, h2, h3, h4, h5, h6)",
            ),
        ].filter(
            (el) =>
                !el.closest(".side-nav-block") &&
                !isInsideSkipList(el) &&
                !isInsideActionCard(el),
        );

    const looseLabel = (value) =>
        normalize(value)
            .replace(/&/g, " and ")
            .replace(/[^a-z0-9]+/g, " ")
            .replace(/\s+/g, " ")
            .trim();

    const matchHeading = (label, headings) => {
        if (!label) return null;
        const loose = looseLabel(label);

        return (
            headings.find((el) => getHeadingLabel(el) === label) ||
            headings.find((el) => looseLabel(getHeadingLabel(el)) === loose) ||
            headings.find((el) => {
                const heading = looseLabel(getHeadingLabel(el));
                return (
                    heading.includes(loose) ||
                    loose.includes(heading) ||
                    heading.startsWith(loose) ||
                    loose.startsWith(heading)
                );
            }) ||
            null
        );
    };

    const findTargetHeading = (linkLabel) => {
        const label = normalize(linkLabel);
        if (!label) return null;

        const headings = collectHeadings();
        const aliases = SCROLL_ALIASES[label] || [];
        const loose = looseLabel(label);

        const matched =
            matchHeading(label, headings) ||
            aliases
                .map((alias) => matchHeading(alias, headings))
                .find(Boolean);
        if (matched) return matched;

        // Headings authored as bold text inside rte blocks (not h1–h6 tags).
        return (
            [...getContentRoot().querySelectorAll(".text-block p, .text-block strong, .text-block b")].find(
                (el) => {
                    if (isInsideSkipList(el) || isInsideActionCard(el)) return false;
                    const text = looseLabel(el.textContent || "");
                    return text === loose || aliases.some((a) => looseLabel(a) === text);
                },
            ) || null
        );
    };

    const ensureHeadingId = (heading, fallbackLabel) => {
        if (heading.id) return heading.id;

        const base =
            slugify(fallbackLabel || heading.textContent || "section") ||
            "section";
        let id = base;
        let i = 2;
        while (document.getElementById(id)) {
            id = `${base}-${i}`;
            i += 1;
        }
        heading.id = id;
        return id;
    };

    const headerOffset = () => {
        const header =
            document.querySelector("sticky-header") ||
            document.querySelector("#header-group") ||
            document.querySelector("header-component") ||
            document.querySelector("header");
        return (header?.getBoundingClientRect().height || 0) + 16;
    };

    const scrollToHeading = (heading) => {
        // Open closed accordion rows before scrolling
        let details = heading.closest?.("details");
        while (details) {
            if (!details.open) details.open = true;
            details = details.parentElement
                ? details.parentElement.closest("details")
                : null;
        }

        const top =
            heading.getBoundingClientRect().top +
            window.scrollY -
            headerOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    };

    const bindScrollClick = (el, id) => {
        if (el.dataset.ccSkipWired === "true") return;
        el.dataset.ccSkipWired = "true";

        el.addEventListener("click", (event) => {
            const target = document.getElementById(id);
            if (!target) return;

            event.preventDefault();
            event.stopPropagation();
            history.pushState(null, "", `#${id}`);
            scrollToHeading(target);
        });
    };

    const wireActionCards = (root = document) => {
        if (!isCustomerCarePage()) return;

        root.querySelectorAll(
            ".group-block:has(> .group-block-content > .icon-block):has(.text-block)",
        ).forEach((card) => {
            const label = card.querySelector(".text-block")?.textContent || "";
            const heading = findTargetHeading(label);
            if (!heading) return;

            const id = ensureHeadingId(heading, label);
            const hash = `#${id}`;

            const groupLink = card.querySelector(":scope > .group-block__link");
            if (groupLink) {
                groupLink.setAttribute("href", hash);
                bindScrollClick(groupLink, id);
            } else {
                bindScrollClick(card, id);
            }

            card.style.cursor = "pointer";
        });
    };

    const setFlexGap = (
        el,
        { gap, columnGap, rowGap, flexDirection, justifyContent },
    ) => {
        if (!el) return;

        el.classList.add("cc-action-card-grid");

        if (gap) {
            el.style.setProperty("gap", gap, "important");
            el.style.setProperty("--gap", gap, "important");
        }
        if (columnGap !== undefined) {
            el.style.setProperty("column-gap", columnGap, "important");
        }
        if (rowGap !== undefined) {
            el.style.setProperty("row-gap", rowGap, "important");
        }
        if (flexDirection) {
            el.style.setProperty("flex-direction", flexDirection, "important");
            el.style.setProperty("--flex-direction", flexDirection, "important");
        }
        if (justifyContent) {
            el.style.setProperty("justify-content", justifyContent, "important");
            el.style.setProperty(
                "--horizontal-alignment",
                justifyContent,
                "important",
            );
        }
    };

    // Theme editor sets inline --gap: max(24px, …) on layout-panel-flex wrappers.
    // CSS alone cannot always win, so force equal row/column gaps on the live DOM.
    const applyActionCardGaps = (root = document) => {
        if (!isCustomerCarePage()) return;

        // The account dashboard's Benefits block is intentionally a vertical
        // heading-and-card stack, not a customer-care action-card row.
        if (document.querySelector("main[data-template='page.account-dashboard']"))
            return;

        document.body.classList.add("cc-customer-care");

        const cards = [
            ...root.querySelectorAll(
                ".group-block:has(> .group-block-content > .icon-block):has(.text-block)",
            ),
        ].filter((card) => !isInfoCard(card));

        if (!cards.length) return;

        const rowContainers = new Set();
        const columnContainers = new Set();

        cards.forEach((card) => {
            card.style.setProperty("flex", "1 1 0", "important");
            card.style.setProperty("width", "auto", "important");
            card.style.setProperty("min-width", "0", "important");
            card.style.setProperty("max-width", "none", "important");
            card.style.setProperty("--size-style-width", "auto", "important");
            card.style.setProperty("margin", "0", "important");

            const row = card.parentElement;
            if (!row?.classList.contains("group-block-content")) return;

            if (normalize(card.textContent || "") === "track my order") {
                card.style.setProperty("flex", "0 0 auto", "important");
                card.style.setProperty("width", "100%", "important");
                card.style.setProperty("max-width", "100%", "important");
                card.style.setProperty(
                    "--size-style-width",
                    "100%",
                    "important",
                );
                row.style.setProperty("align-items", "stretch", "important");
                setFlexGap(row, {
                    gap: "1rem",
                    columnGap: "0",
                    rowGap: "1rem",
                    flexDirection: "column",
                    justifyContent: "center",
                });
                return;
            }

            rowContainers.add(row);

            const rowGroup = row.parentElement;
            const column = rowGroup?.parentElement;
            if (column?.classList.contains("group-block-content")) {
                columnContainers.add(column);
            }

            const sectionColumn = card.closest(
                ".section-content-wrapper.layout-panel-flex",
            );
            if (sectionColumn && cards.length > 1) {
                columnContainers.add(sectionColumn);
            }
        });

        rowContainers.forEach((row) => {
            setFlexGap(row, {
                gap: ACTION_CARD_GRID_GAP,
                columnGap: ACTION_CARD_GRID_GAP,
                rowGap: "0",
                flexDirection: "row",
                justifyContent: "flex-start",
            });
        });

        columnContainers.forEach((column) => {
            setFlexGap(column, {
                gap: ACTION_CARD_GRID_GAP,
                rowGap: ACTION_CARD_GRID_GAP,
                columnGap: "0",
            });
        });
    };

    // "Chat now" inside the grey info card is authored as plain text.
    // Turn it into a real link so it is clickable (styled via customer-care.css).
    const CHAT_LABELS = new Set(["chat now"]);
    const CHAT_HREF = "/pages/contact";

    const isInfoCard = (card) =>
        !!card.querySelector(
            ".text-block ~ .text-block, .text-block br, .text-block :is(h1, h2, h3, h4, h5, h6), .text-block strong",
        );

    const isFeedbackCard = (card) => {
        if (!card?.classList?.contains("group-block")) return false;
        if (card.querySelector(".icon-block")) return false;
        if (!card.querySelector(".text-block a")) return false;
        return /feedback to share/i.test(card.textContent || "");
    };

    const isQuestionCard = (card) => {
        if (!card?.classList?.contains("group-block")) return false;
        if (!card.querySelector(":scope > .group-block-content > .icon-block"))
            return false;
        if (!isInfoCard(card)) return false;
        return /have a question/i.test(card.textContent || "");
    };

    const isPairedInfoCardRow = (card) => {
        const parent = card.parentElement;
        if (!parent?.classList.contains("group-block-content")) return false;

        const iconCards = [
            ...parent.querySelectorAll(
                ":scope > .group-block:has(> .group-block-content > .icon-block):has(.text-block)",
            ),
        ];
        return iconCards.length > 1;
    };

    let greyCardWidthObserver = null;

    const syncGreyCardWidths = () => {
        if (!isCustomerCarePage()) return;

        const cards = [...document.querySelectorAll(".group-block")];
        const feedbackCard = cards.find(isFeedbackCard);
        const questionCard = cards.find(
            (card) => isQuestionCard(card) && !isPairedInfoCardRow(card),
        );

        if (!feedbackCard || !questionCard) return;

        const width = Math.round(feedbackCard.getBoundingClientRect().width);
        if (width <= 0) return;

        questionCard.style.setProperty("width", `${width}px`, "important");
        questionCard.style.setProperty("max-width", `${width}px`, "important");
        questionCard.style.setProperty("min-width", "0", "important");
        questionCard.style.setProperty("flex", "0 0 auto", "important");
        questionCard.style.setProperty(
            "--size-style-width",
            `${width}px`,
            "important",
        );
        questionCard.classList.add("cc-synced-grey-card");

        if (!greyCardWidthObserver) {
            greyCardWidthObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(syncGreyCardWidths);
            });
            greyCardWidthObserver.observe(feedbackCard);
        }
    };

    // Chat with us / Call us sit in info-card rows — theme inline --gap wins over CSS.
    const applyInfoCardRowGaps = (root = document) => {
        if (!isCustomerCarePage()) return;

        const rowContainers = new Set();

        [
            ...root.querySelectorAll(
                ".group-block:has(> .group-block-content > .icon-block)",
            ),
        ]
            .filter((card) => isInfoCard(card) && isPairedInfoCardRow(card))
            .forEach((card) => {
                card.style.setProperty("flex", "1 1 0", "important");
                card.style.setProperty("width", "auto", "important");
                card.style.setProperty("min-width", "0", "important");
                card.style.setProperty("max-width", "none", "important");
                card.style.setProperty("--size-style-width", "auto", "important");
                card.style.setProperty("margin", "0", "important");

                const row = card.parentElement;
                if (row?.classList.contains("group-block-content")) {
                    rowContainers.add(row);
                }
            });

        rowContainers.forEach((row) => {
            row.classList.add("cc-info-card-row");
            setFlexGap(row, {
                gap: ACTION_CARD_GRID_GAP,
                columnGap: ACTION_CARD_GRID_GAP,
                rowGap: "0",
                flexDirection: "row",
                justifyContent: "flex-start",
            });
        });
    };

    const findLabelHost = (card) => {
        const matches = [
            ...card.querySelectorAll(
                ".text-block :is(p, span, strong, b, div, li)",
            ),
        ].filter((el) => CHAT_LABELS.has(normalize(el.textContent || "")));
        // Deepest element whose whole text is the label (no child holds the same text).
        return (
            matches.find(
                (el) =>
                    ![...el.children].some((child) =>
                        CHAT_LABELS.has(normalize(child.textContent || "")),
                    ),
            ) || null
        );
    };

    const wireChatLinks = (root = document) => {
        if (!isCustomerCarePage()) return;

        root.querySelectorAll(
            ".group-block:has(> .group-block-content > .icon-block)",
        ).forEach((card) => {
            if (!isInfoCard(card)) return;

            // Already a link (authored in the editor)? Nothing to do — CSS styles it.
            const hasLink = [...card.querySelectorAll(".text-block a")].some(
                (a) => CHAT_LABELS.has(normalize(a.textContent || "")),
            );
            if (hasLink) return;

            const host = findLabelHost(card);
            if (!host || host.dataset.ccChatWired === "true") return;

            const link = document.createElement("a");
            link.textContent = (host.textContent || "").trim();
            link.setAttribute("href", CHAT_HREF);
            host.textContent = "";
            host.appendChild(link);
            host.dataset.ccChatWired = "true";
        });
    };

    // Page-scoped contact form tweaks: rename the message placeholder and add an
    // "Order Number" field (between Phone and Message, submitted as contact[Order Number]).
    const enhanceContactForm = (root = document) => {
        if (!isCustomerCarePage()) return;

        root.querySelectorAll(".contact-form__form").forEach((form) => {
            const textarea = form.querySelector(
                ".contact-form__input--textarea",
            );
            if (!textarea) return;

            textarea.setAttribute("placeholder", "Message");

            if (form.querySelector('[name="contact[Order Number]"]')) return;

            const anchor =
                textarea.previousElementSibling?.tagName === "LABEL"
                    ? textarea.previousElementSibling
                    : textarea;

            const fieldId = `${form.id || "ContactForm"}-order-number`;

            const label = document.createElement("label");
            label.className = "visually-hidden";
            label.setAttribute("for", fieldId);
            label.textContent = "Order Number";

            const input = document.createElement("input");
            input.type = "text";
            input.id = fieldId;
            input.className = "contact-form__input";
            input.name = "contact[Order Number]";
            input.placeholder = "Order Number";
            input.autocomplete = "off";
            input.required = true;
            input.setAttribute("aria-required", "true");

            anchor.before(label, input);
        });
    };

    // Country/region picker for the phone field via intl-tel-input, lazy-loaded
    // (only when this page's phone field exists) so other pages stay unaffected.
    const ITI_BASE = "https://cdn.jsdelivr.net/npm/intl-tel-input@19/build";
    let itiLoader = null;

    const loadIntlTelInput = () => {
        if (itiLoader) return itiLoader;

        itiLoader = new Promise((resolve, reject) => {
            if (window.intlTelInput) {
                resolve(window.intlTelInput);
                return;
            }

            const css = document.createElement("link");
            css.rel = "stylesheet";
            css.href = `${ITI_BASE}/css/intlTelInput.css`;
            document.head.appendChild(css);

            const script = document.createElement("script");
            script.src = `${ITI_BASE}/js/intlTelInput.min.js`;
            script.onload = () => resolve(window.intlTelInput);
            script.onerror = reject;
            document.head.appendChild(script);
        });

        return itiLoader;
    };

    const enhancePhoneField = (root = document) => {
        if (!isCustomerCarePage()) return;

        const input = root.querySelector(
            '.contact-form__form input[type="tel"]',
        );
        if (!input || input.dataset.ccTelWired === "true") return;
        input.dataset.ccTelWired = "true";

        // Shopify's default tel pattern only allows digits/hyphens, which rejects
        // the international format ("+995 551 ..."). Relax it for this page so the
        // "+", spaces and parens are accepted.
        input.setAttribute("pattern", "[+0-9()\\s-]*");

        loadIntlTelInput()
            .then((intlTelInput) => {
                if (typeof intlTelInput !== "function") return;

                const iti = intlTelInput(input, {
                    initialCountry: "ca",
                    nationalMode: false,
                    countrySearch: false,
                    utilsScript: `${ITI_BASE}/js/utils.js`,
                });

                // Seed the dial code inside the phone field on load (e.g. "+1").
                // intl-tel-input itself keeps it in sync when the country changes,
                // so we must NOT add our own countrychange handler (that duplicated
                // the code, e.g. "+49 +49").
                const dial = iti.getSelectedCountryData()?.dialCode || "";
                if (dial && input.value.trim() === "") {
                    input.value = `+${dial}`;
                }

                // Expose the picker instance so the AJAX submit handler can
                // normalize the number to E.164 before sending.
                input.ccIti = iti;
            })
            .catch(() => {
                input.dataset.ccTelWired = "";
            });
    };

    // Fallback success card shown when the POST redirects off this page (e.g.
    // Shopify sends the visitor to the home page) so the returned HTML has no
    // .contact-form__success of its own.
    const buildSuccessCard = () => {
        const box = document.createElement("div");
        box.className = "contact-form__success";
        box.setAttribute("tabindex", "-1");
        box.innerHTML =
            '<h3 class="contact-form__success-title">Success</h3>' +
            '<p class="contact-form__success-text">Thank you. The form has been submitted.</p>';
        return box;
    };

    // Keep the visitor on the page after submitting. We can't use a background
    // fetch here because Shopify's spam-protection captcha intercepts the submit
    // and re-submits the form natively (a full-page redirect). Instead we point
    // the form's native submission at a hidden iframe, so the captcha flow runs
    // normally but the response loads off-screen — the top page never navigates.
    const wireContactAjax = (root = document) => {
        if (!isCustomerCarePage()) return;

        root.querySelectorAll(".contact-form__form").forEach((form) => {
            if (form.dataset.ccAjax === "true") return;
            form.dataset.ccAjax = "true";

            const frameName = `cc-contact-sink-${Math.random()
                .toString(36)
                .slice(2)}`;
            const frame = document.createElement("iframe");
            frame.name = frameName;
            frame.title = "";
            frame.setAttribute("aria-hidden", "true");
            frame.setAttribute("tabindex", "-1");
            frame.style.cssText =
                "position:absolute;width:0;height:0;border:0;overflow:hidden;opacity:0;pointer-events:none;";
            document.body.appendChild(frame);

            form.setAttribute("target", frameName);

            let submitting = false;

            // Clear the custom phone message as soon as the visitor edits it.
            const telField = form.querySelector('input[type="tel"]');
            if (telField) {
                telField.addEventListener("input", () =>
                    telField.setCustomValidity(""),
                );
            }

            form.addEventListener("submit", (event) => {
                const tel = form.querySelector('input[type="tel"]');
                if (tel && tel.ccIti) {
                    // The field is pre-seeded with the dial code (e.g. "+1"), so
                    // require at least one digit beyond it — otherwise treat the
                    // phone as empty and block the submit like a required field.
                    const dial =
                        tel.ccIti.getSelectedCountryData?.()?.dialCode || "";
                    const digits = (tel.value.match(/\d/g) || []).join("");
                    if (digits.length <= dial.length) {
                        event.preventDefault();
                        tel.setCustomValidity(
                            "Please enter your phone number.",
                        );
                        tel.reportValidity();
                        return;
                    }
                    tel.setCustomValidity("");

                    // Normalize the phone number to E.164 before it is sent.
                    try {
                        const full = tel.ccIti.getNumber();
                        if (full) tel.value = full;
                    } catch (error) {
                        /* keep the typed value */
                    }
                }

                const submit = form.querySelector('[type="submit"]');
                if (submit) submit.disabled = true;
                submitting = true;
                // Do NOT preventDefault — let the native (and, if present, the
                // captcha-driven) submission run, but into the hidden iframe.
            });

            frame.addEventListener("load", () => {
                // Ignore the initial about:blank load; only react post-submit.
                if (!submitting) return;
                submitting = false;

                const submit = form.querySelector('[type="submit"]');

                let doc = null;
                try {
                    doc = frame.contentDocument;
                } catch (error) {
                    doc = null; // cross-origin (shouldn't happen) — treat as sent
                }

                const success = doc?.querySelector(".contact-form__success");
                const error = doc?.querySelector(".contact-form__error");

                // Validation/captcha failure re-renders the form with an error
                // block and no success block — surface it and allow a retry.
                if (error && !success) {
                    const current = form.querySelector(".contact-form__error");
                    const node = document.importNode(error, true);
                    if (current) current.replaceWith(node);
                    else form.prepend(node);
                    node.focus?.();
                    if (submit) submit.disabled = false;
                    return;
                }

                const node = buildSuccessCard();
                form.replaceChildren(node);
                node.focus?.();
            });
        });
    };

    const activateSizeChartPanel = (chart, name) => {
        const tabs = [...chart.querySelectorAll(".size-chart__tab")];
        const panels = [...chart.querySelectorAll(".size-chart__panel")];

        tabs.forEach((tab) => {
            const active = tab.dataset.panel === name;
            tab.classList.toggle("is-active", active);
            tab.setAttribute("aria-selected", active ? "true" : "false");
        });
        panels.forEach((panel) => {
            panel.classList.toggle("is-active", panel.dataset.panel === name);
        });
    };

    document.addEventListener(
        "click",
        (event) => {
            if (!isCustomerCarePage()) return;

            const tab = event.target.closest?.(".size-chart__tab");
            const chart = tab?.closest("[data-size-chart]");
            if (!chart) return;

            event.preventDefault();
            event.stopPropagation();
            activateSizeChartPanel(chart, tab.dataset.panel);
        },
        true,
    );

    const wireSkipLinks = (root = document) => {
        if (!isCustomerCarePage()) return;

        const orderShippingTargets = {
            "available-shipping-options":
                '.text-block[class*="__shipping-options-heading"] h2',
            "shipping-faqs":
                '.text-block[class*="__shipping-faqs-heading"] h2',
            "ordering-faqs":
                '.text-block[class*="__ordering-faqs-heading"] h2',
        };

        Object.entries(orderShippingTargets).forEach(([id, selector]) => {
            const heading = document.querySelector(selector);
            if (heading) heading.id = id;
        });

        root.querySelectorAll(".text-block ul:has(a) li").forEach((item) => {
            const link = item.querySelector("a");
            if (!link) return;

            const hash = (link.getAttribute("href") || "").replace(/^#/, "");
            const heading =
                document.getElementById(hash) ||
                findTargetHeading(link.textContent || "");
            if (!heading) return;

            const id = ensureHeadingId(heading, link.textContent || "");
            link.setAttribute("href", `#${id}`);
            bindScrollClick(link, id);
        });
    };

    const run = () => {
        markSubheads();
        wireActionCards();
        wireSkipLinks();
        applyActionCardGaps();
        applyInfoCardRowGaps();
        syncGreyCardWidths();
        wireChatLinks();
        enhanceContactForm();
        enhancePhoneField();
        wireContactAjax();
    };

    if (document.readyState === "loading") {
        document.addEventListener(
            "DOMContentLoaded",
            () => {
                run();
            },
            { once: true },
        );
    } else {
        run();
    }

    // Theme editor re-runs on section load; on the live storefront retry once
    // after full load in case blocks render after DOMContentLoaded.
    window.addEventListener("load", run, { once: true });
    window.addEventListener("resize", syncGreyCardWidths);

    document.addEventListener("shopify:section:load", run);
    document.addEventListener("shopify:block:select", run);

    document.addEventListener(
        "toggle",
        (event) => {
            if (event.target instanceof HTMLDetailsElement) markSubheads();
        },
        true,
    );
})();
